// Get a Quote modal. Opens from any element carrying `data-qm-quote-open`
// (e.g. a "Get a Quote" CTA on the boat VDP).
//
// On submit: validates, runs a Cloudflare Turnstile challenge, POSTs the
// fields to the form-handling Worker (`taylorsBoats.formsWorkerUrl`) as
// `form_type=quote`, and on a confirmed `{status:"ok"}` response reveals
// the success state + pushes a `lead_submit` event to dataLayer so the GTM
// lead tags (GA4 / Meta / Google Ads / TikTok) fire exactly as they do for
// the page-level quote form handled by tb-forms.js.
//
// The boat context (year / make / model / stock / product id) comes from
// the VDP and is baked into hidden form inputs by the section's Liquid, so
// the modal never asks the visitor to re-enter the boat.
//
// Self-contained (does NOT use the `data-tb-worker-form` path in
// tb-forms.js, and does NOT import it) because the modal owns its own
// success-panel UX, focus trap, and reset-on-reopen lifecycle. The
// `lead_submit` payload shape (event_id + boatContext) is replicated
// locally to stay byte-for-byte compatible with tb-forms.js. The Turnstile
// API loader is shared (snippets/tb-forms-captcha.liquid); only the
// per-widget mount is local.

(function () {
  if (customElements.get('qm-quote-modal')) return;

  const tb = (window.taylorsBoats = window.taylorsBoats || {});

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  // Mirrors tb-forms.js genEventId(): one id per submit, shared between the
  // server-side Meta CAPI Lead (sent by the Worker) and the browser Meta
  // Lead tag (fired off the dataLayer push) so Meta dedups into one lead.
  function genEventId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'tb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  // Mirrors tb-forms.js boatContext(): build the boat fields from the
  // page-level `taylorsBoats.boat` object the VDP exposes.
  function boatContext() {
    const boat = (window.taylorsBoats && window.taylorsBoats.boat) || {};
    return {
      boat_product_id: boat.product_id || '',
      boat_stock_number: boat.stock_number || '',
      boat_year: boat.year || '',
      boat_make: boat.make || '',
      boat_model: boat.model || '',
    };
  }

  class QmQuoteModal extends HTMLElement {
    constructor() {
      super();
      /** @type {HTMLFormElement | null} */
      this._form = null;
      /** @type {HTMLElement | null} */
      this._success = null;
      /** @type {HTMLElement | null} */
      this._lastFocus = null;
      /** @type {string | number | null} */
      this._widgetId = null;
      /** @type {HTMLButtonElement | null} */
      this._submitBtn = null;
      this._submitLabel = '';
      this._pending = false;
      this._onKeydown = this._onKeydown.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
    }

    connectedCallback() {
      const form = this.querySelector('[data-qm-form]');
      this._form = form instanceof HTMLFormElement ? form : null;
      const success = this.querySelector('[data-qm-success]');
      this._success = success instanceof HTMLElement ? success : null;
      const submit = this._form && this._form.querySelector('button[type="submit"]');
      this._submitBtn = submit instanceof HTMLButtonElement ? submit : null;
      this._submitLabel = this._submitBtn ? this._submitBtn.textContent : '';

      this.addEventListener('click', this._onClick);
      if (this._form) this._form.addEventListener('submit', this._onSubmit);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      if (this._form) this._form.removeEventListener('submit', this._onSubmit);
      document.removeEventListener('keydown', this._onKeydown);
    }

    /** @param {Element | null} trigger */
    open(trigger) {
      this._lastFocus = trigger instanceof HTMLElement ? trigger : null;

      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKeydown);

      this._resetToForm();
      this._mountTurnstile();

      const firstField = this.querySelector('input:not([type="hidden"]), textarea, button');
      if (firstField instanceof HTMLElement) firstField.focus();
    }

    /**
     * @param {string} name
     * @param {string} value
     */
    _setHiddenInput(name, value) {
      if (!this._form) return;
      let input = this._form.querySelector(`input[name="${name}"]`);
      if (!(input instanceof HTMLInputElement)) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        this._form.appendChild(input);
      }
      input.value = value;
    }

    _mountTurnstile() {
      if (!this._form || this._widgetId != null) return;
      if (!tb.turnstileSiteKey) return;
      if (typeof turnstile === 'undefined' || !turnstile.render) return;
      let mount = this._form.querySelector('[data-qm-turnstile]');
      if (!mount) {
        mount = document.createElement('div');
        mount.setAttribute('data-qm-turnstile', '');
        this._form.appendChild(mount);
      }
      try {
        this._widgetId = turnstile.render(mount, {
          sitekey: tb.turnstileSiteKey,
          appearance: 'interaction-only',
          action: 'quote',
        });
      } catch (e) {
        console.error('[qm-quote-modal] Turnstile mount failed:', e);
      }
    }

    /** @returns {Promise<string|null>} */
    _getTurnstileToken() {
      return new Promise((resolve) => {
        if (this._widgetId == null || typeof turnstile === 'undefined' || !turnstile.execute) {
          resolve(null);
          return;
        }
        const existing = turnstile.getResponse(this._widgetId);
        if (existing) {
          resolve(existing);
          return;
        }
        try {
          turnstile.execute(this._widgetId, {
            callback: (token) => resolve(token),
            'error-callback': () => resolve(null),
          });
        } catch (e) {
          resolve(null);
        }
      });
    }

    _resetTurnstile() {
      if (typeof turnstile !== 'undefined' && turnstile.reset && this._widgetId != null) {
        try {
          turnstile.reset(this._widgetId);
        } catch (e) {
          /* ignore */
        }
      }
    }

    /** @param {string} eventId */
    _pushLeadSubmit(eventId) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(
        Object.assign(
          {
            event: 'lead_submit',
            form_type: 'quote',
            event_id: eventId || '',
          },
          boatContext(),
        ),
      );
    }

    /** @param {boolean} isSubmitting */
    _setSubmitting(isSubmitting) {
      if (!this._submitBtn) return;
      this._submitBtn.disabled = isSubmitting;
      this._submitBtn.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
      this._submitBtn.textContent = isSubmitting ? 'Sending…' : this._submitLabel;
    }

    /** @param {string} message */
    _showError(message) {
      const el = this.querySelector('[data-qm-error]');
      if (el instanceof HTMLElement) {
        el.textContent = message;
        el.hidden = false;
      }
    }

    _clearError() {
      const el = this.querySelector('[data-qm-error]');
      if (el instanceof HTMLElement) {
        el.textContent = '';
        el.hidden = true;
      }
    }

    close() {
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKeydown);
      if (this._lastFocus) this._lastFocus.focus();
    }

    _resetToForm() {
      if (!this._form || !this._success) return;
      this._form.hidden = false;
      this._success.hidden = true;
      this._form.reset();
      this._clearError();
    }

    _showSuccess() {
      if (!this._form || !this._success) return;
      this._form.hidden = true;
      this._success.hidden = false;
      this._success.setAttribute('tabindex', '-1');
      this._success.focus();
    }

    /** @param {Event} event */
    _onClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-qm-close]')) {
        event.preventDefault();
        this.close();
      }
    }

    /** @param {KeyboardEvent} event */
    _onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'Tab') {
        this._trapFocus(event);
      }
    }

    /** @param {KeyboardEvent} event */
    _trapFocus(event) {
      /** @type {HTMLElement[]} */
      const focusables = [];
      this.querySelectorAll(FOCUSABLE).forEach((el) => {
        if (el instanceof HTMLElement && !el.hidden && el.offsetParent !== null) {
          focusables.push(el);
        }
      });
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /** @param {SubmitEvent} event */
    async _onSubmit(event) {
      event.preventDefault();
      if (!this._form || this._pending) return;
      // reportValidity() shows the native bubble and moves focus, but does not
      // set aria-invalid. Flag each control so screen readers can identify which
      // fields are in error (WCAG 3.3.1); cleared on the next valid pass.
      this._form.querySelectorAll('input, select, textarea').forEach((field) => {
        if (!field.willValidate) return;
        if (field.checkValidity()) {
          field.removeAttribute('aria-invalid');
        } else {
          field.setAttribute('aria-invalid', 'true');
        }
      });
      if (!this._form.checkValidity()) {
        this._form.reportValidity();
        return;
      }

      // No Worker URL configured (dev / pre-launch): show the success
      // panel so the modal still demos, but nothing is captured.
      if (!tb.formsWorkerUrl) {
        this._showSuccess();
        return;
      }

      this._pending = true;
      this._clearError();
      this._setSubmitting(true);

      const token = await this._getTurnstileToken();
      if (token != null) this._setHiddenInput('cf-turnstile-response', token);

      const eventId = genEventId();
      const fd = new FormData(this._form);
      fd.append('tb_event_id', eventId);
      fd.append('tb_event_source_url', location.href);

      let res, data;
      try {
        res = await fetch(tb.formsWorkerUrl, { method: 'POST', body: fd });
      } catch (e) {
        this._pending = false;
        this._setSubmitting(false);
        this._showError('Network error. Please check your connection and try again.');
        this._resetTurnstile();
        return;
      }
      try {
        data = await res.json();
      } catch (e) {
        data = {};
      }
      this._pending = false;
      this._setSubmitting(false);

      if (res.ok && data.status === 'ok') {
        if (data.form_type) this._pushLeadSubmit(eventId);
        this._showSuccess();
      } else {
        this._showError((data && data.error) || 'Something went wrong. Please try again.');
        this._resetTurnstile();
      }
    }
  }

  customElements.define('qm-quote-modal', QmQuoteModal);

  /**
   * @param {Element} trigger
   * @returns {QmQuoteModal | null}
   */
  function findModal(trigger) {
    const id = trigger.getAttribute('data-qm-quote-target');
    if (id) {
      const byId = document.getElementById(id);
      if (byId instanceof QmQuoteModal) return byId;
    }
    const fallback = document.querySelector('qm-quote-modal');
    return fallback instanceof QmQuoteModal ? fallback : null;
  }

  document.addEventListener('click', function (event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('[data-qm-quote-open]');
    if (!trigger) return;
    const modal = findModal(trigger);
    if (!modal) return;
    event.preventDefault();
    modal.open(trigger);
  });
})();
